import Vue from "vue";
import _, { result } from "lodash";

import { findByIds } from "@/services/local/points-service";
import * as typesService from "@/services/local/types-service";
import * as pointsService from "@/services/local/points-service";
import * as chakrasService from "@/services/local/chakras-service";
import * as symptomsService from "@/services/local/symptoms-service";
import * as pathogensService from "@/services/local/pathogens-service";
import * as sequencesService from "@/services/local/sequences-service";
import * as flowTypesService from "@/services/local/flow-types-service";
import * as categoriesService from "@/services/local/categories-service";
import * as chromosomesService from "@/services/local/chromosomes-service";
import * as pointsOrderService from "@/services/local/points-order-service";
import * as paresService from "@/services/local/pares-biomagneticos-service";
import * as oligoelementsService from "@/services/local/oligoelements-service";
import * as potentialSpacesService from "@/services/local/potential-spaces-service";
import { getAll as getFilteredItems } from "@/services/local/filtered-therapy-items";
import * as tumoralPhenomenaService from "@/services/local/tumoral-phenomena-service";
import * as vascularPhenomenaService from "@/services/local/vascular-phenomena-service";
import * as complementaryTherapyService from "@/services/local/complementary-therapy-service";

import { _ACTIONS, _MUTATIONS } from "./types";
import { SlugEnum } from "@/domains/therapy/steps/mount-sequence/domain/slug-enum";

export const actions = {
  [_ACTIONS.TOGGLE_ITEM_FROM_PROPERTY_TO_SEQUENCE](
    { commit, state },
    { item, selectedProperty }
  ) {
    const isItemSelected = state.selectedItems.some(
      (selectedItem) =>
        selectedItem.id === item.id &&
        selectedItem.property.slug === selectedProperty.slug
    );

    if (!isItemSelected) {
      commit(_MUTATIONS.ADD_SELECTED_ITEM, {
        id: item.id,
        name: item.name,
        selected: item.selected,
        slug: item.slug,
        property: {
          name: selectedProperty.name,
          slug: selectedProperty.slug,
        },
      });
    } else {
      commit(_MUTATIONS.REMOVE_SELECTED_ITEM, item);
    }
  },
  [_ACTIONS.CLEAR_SELECTED_ITEMS]({ commit }) {
    commit(_MUTATIONS.SET_SELECTED_ITEMS, []);
    // Limpa o ID e nome do template quando os itens selecionados são limpos
    commit("diagnosis/setTherapyTemplateId", null, { root: true });
    commit("diagnosis/setSelectedTemplateName", null, { root: true });
  },
  async [_ACTIONS.MOUNT_SEQUENCE]({ state, dispatch, commit }) {
    try {
      commit("therapy/saveSequence", state.selectedItems, { root: true });

      dispatch("loading/openDialog", null, { root: true });
      const stepsFunctions = new Map([
        ["type", (item) => dispatch(_ACTIONS.STEPS_TYPE, item)],
        ["symptom", (item) => dispatch(_ACTIONS.STEPS_OTHER_PROPERTIES, item)],
        ["pathogen", (item) => dispatch(_ACTIONS.STEPS_OTHER_PROPERTIES, item)],
        ["category", (item) => dispatch(_ACTIONS.STEPS_OTHER_PROPERTIES, item)],
      ]);

      const sequence = state.selectedItems.map(async (item) => {
        const functionToExecute = stepsFunctions.get(item.property.slug);

        const steps = await functionToExecute(item);

        return {
          name: item.name,
          explanation: item.explanation,
          propertyLabel: item.property.name,
          steps,
        };
      });
      const mountedSequence = await Promise.all(sequence);
      commit(_MUTATIONS.SET_SEQUENCE, mountedSequence);
    } catch (error) {
      Vue.prototype.$errorHandler(error);
    } finally {
      dispatch("loading/closeDialog", null, { root: true });
    }
  },

  /**
   * Pares por tipo
   * @param state
   * @param item
   * @returns {Promise<*>}
   */
  async [_ACTIONS.STEPS_TYPE]({ state }, item) {
    const typeId = item.id;
    let sequenceType;

    if (!!item?.typeSequenceId) {
      sequenceType = await sequencesService.findOne(item.typeSequenceId);
    } else {
      const filteredItems = await getFilteredItems();
      const typeSequences = await sequencesService.findByTypeId(typeId);

      if (filteredItems && filteredItems.length > 0 && filteredItems[0].data) {
        const filteredSequences = filteredItems[0].data.sequences;

        const result = typeSequences.filter((seq) =>
          filteredSequences.some((item) => item === seq.id)
        );
        sequenceType = result[0] || null;
      }
      if (!sequenceType) {
        const seq = typeSequences.find((s) => s.isDefault);
        sequenceType = seq;
      }
    }

    if (!sequenceType) {
      const mapFindAllByCustomType = new Map([
        [SlugEnum.FENOMENOS_TUMORAIS, tumoralPhenomenaService.findAll],
        [SlugEnum.FENOMENOS_VASCULARES, vascularPhenomenaService.findAll],
        [SlugEnum.ESPACOS_POTENCIAIS, potentialSpacesService.findAll],
        [SlugEnum.TIPOS_DE_FLUXO, flowTypesService.findAll],
        [SlugEnum.OLIGOELEMENTOS, oligoelementsService.findAll],
        [SlugEnum.CHAKRAS, chakrasService.findAll],
        [SlugEnum.CROMOSSOMOS, chromosomesService.findAll],
        [SlugEnum.TERAPIAS_COMPLEMENTARES, complementaryTherapyService.findAll],
        // 🔥 CORREÇÃO: Adiciona suporte para tipos de tracking points
        [SlugEnum.RASTREIO, pointsService.findAllTrackingPoints],
        [SlugEnum.RESERVATORIO, pointsService.findAllTrackingPoints],
      ]);

      const findAllCustomTypeFunction = mapFindAllByCustomType.get(item.slug);

      let results = [];
      console.log("Has NO Sequence, Show All Items");
      if (findAllCustomTypeFunction) {
        console.log("Type is custom type", item.slug);
        results = await findAllCustomTypeFunction();

        // 🔥 CORREÇÃO: Tratamento especial para tracking points (RASTREIO e RESERVATORIO)
        if (
          item.slug === SlugEnum.RASTREIO ||
          item.slug === SlugEnum.RESERVATORIO
        ) {
          const filteredItems = await getFilteredItems();
          if (
            filteredItems &&
            filteredItems.length > 0 &&
            filteredItems[0].data
          ) {
            const filteredTrackingPoints = filteredItems[0].data.trackingPoints;
            results = results.filter((point) =>
              filteredTrackingPoints.some((tp) => tp === point.id)
            );
          }

          // 🔥 Filtra apenas pontos de rastreio que possuem ao menos um ponto de impactação
          const trackingPointsWithImpaction = [];
          for (const point of results) {
            const pointsOrder = await pointsOrderService.findByTrackingPoint([
              point.id,
            ]);
            if (pointsOrder && pointsOrder.length > 0) {
              trackingPointsWithImpaction.push(point);
            }
          }

          const mappedResults = trackingPointsWithImpaction.map((point) => ({
            title: point.name,
            slug: item.slug,
            trackingPoint: {
              id: point.id,
              name: point.name,
            },
            additionalData: {
              typeId: item.id,
            },
          }));

          console.log(
            `[${item.slug}] Tracking points com impactação:`,
            mappedResults
          );
          return mappedResults;
        }
      } else {
        console.log("Type is par type", item.slug);
        const findByType = async () => {
          const type = await typesService.getBySlug(item.slug);

          // 🛡️ Proteção: se tipo não existe no IndexedDB local, retorna array vazio
          if (!type || !type.id) {
            //console.warn(
            //  `⚠️ Tipo '${item.slug}' não encontrado no IndexedDB local`
            //);
            return [];
          }

          return paresService.findParesByTypeId(type.id);
        };
        results = await findByType();
      }

      results.sort((a, b) => {
        if (a.order > b.order) return 1;
        if (a.order < b.order) return -1;
        return 0;
      });

      return results.map((result) => {
        const customTitleMap = new Map([
          ["cromossomos", `Cromossomo(a) ${result.order}`],
        ]);
        return {
          title: customTitleMap.get(item.slug) || result.name,
          type: result,
          slug: item.slug,
        };
      });
    }

    console.log("Has Sequence, Show Filtered Items");

    // INICIO TIPO CUSTOMIZADO --------------------------------------------------

    const mapFindFilteredByCustomType = new Map([
      [
        SlugEnum.FENOMENOS_TUMORAIS,
        async () => {
          const results = await tumoralPhenomenaService.findAll();
          return sequenceType.tumoralPhenomenonOrder.map((order) => {
            const item = results.find(
              (item) => item.id === order.tumoralPhenomenonId
            );
            return {
              title: item?.name || "Desconhecido",
              type: item,
              slug: SlugEnum.FENOMENOS_TUMORAIS,
            };
          });
        },
      ],
      [
        SlugEnum.FENOMENOS_VASCULARES,
        async () => {
          const results = await vascularPhenomenaService.findAll();
          return sequenceType.vascularPhenomenonOrder.map((order) => {
            const item = results.find(
              (item) => item.id === order.vascularPhenomenonId
            );
            return {
              title: item?.name || "Desconhecido",
              type: item,
              slug: SlugEnum.FENOMENOS_VASCULARES,
            };
          });
        },
      ],
      [
        SlugEnum.ESPACOS_POTENCIAIS,
        async () => {
          const results = await potentialSpacesService.findAll();
          return sequenceType.potentialSpaceOrder.map((order) => {
            const item = results.find(
              (item) => item.id === order.potentialSpaceId
            );
            return {
              title: item?.name || "Desconhecido",
              type: item,
              slug: SlugEnum.ESPACOS_POTENCIAIS,
            };
          });
        },
      ],
      [
        SlugEnum.TIPOS_DE_FLUXO,
        async () => {
          const results = await flowTypesService.findAll();
          return sequenceType.flowTypeOrder.map((order) => {
            const item = results.find((item) => item.id === order.flowTypeId);
            return {
              title: item?.name || "Desconhecido",
              type: item,
              slug: SlugEnum.TIPOS_DE_FLUXO,
            };
          });
        },
      ],
      [
        SlugEnum.OLIGOELEMENTOS,
        async () => {
          const results = await oligoelementsService.findAll();
          return sequenceType.oligoelementOrder.map((order) => {
            const item = results.find(
              (item) => item.id === order.oligoelementId
            );
            return {
              title: item?.name || "Desconhecido",
              type: item,
              slug: SlugEnum.OLIGOELEMENTOS,
            };
          });
        },
      ],
      [
        SlugEnum.CROMOSSOMOS,
        async () => {
          const results = await chromosomesService.findAll();
          return sequenceType.chromosomeOrder.map((order) => {
            const item = results.find((item) => item.id === order.chromosomeId);
            return {
              title: item?.description || "Desconhecido",
              type: item,
              slug: SlugEnum.CROMOSSOMOS,
            };
          });
        },
      ],
      [
        SlugEnum.CHAKRAS,
        async () => {
          const results = await chakrasService.findAll();
          return sequenceType.chakraOrder.map((chakraOrder) => {
            const chakra = results.find(
              (chakra) => chakra.id === chakraOrder.chakraId
            );
            return {
              title: chakra?.name || "Desconhecido",
              type: chakra,
              slug: SlugEnum.CHAKRAS,
            };
          });
        },
      ],
      [
        SlugEnum.TERAPIAS_COMPLEMENTARES,
        async () => {
          const results = await complementaryTherapyService.findAll();
          return sequenceType.complementarTherapyOrder.map((order) => {
            const item = results.find(
              (item) => item.id === order.complementarTherapyId
            );
            return {
              title: item?.name || "Desconhecido",
              type: item,
              slug: SlugEnum.TERAPIAS_COMPLEMENTARES,
            };
          });
        },
      ],
    ]);

    const findFilteredByCustomTypeFunction = mapFindFilteredByCustomType.get(
      item.slug
    );

    if (findFilteredByCustomTypeFunction) {
      const results = await findFilteredByCustomTypeFunction();

      console.log("findFilteredByCustomTypeFunction results:", results);

      if (!results) {
        return;
      }

      return results;
    }

    // FIM TIPO CUSTOMIZADO --------------------------------------------------

    if (
      item.slug === SlugEnum.RASTREIO ||
      item.slug === SlugEnum.RESERVATORIO
    ) {
      console.log("Is Point SLUG");
      const ids = sequenceType.pointOrder
        .sort((a, b) => {
          if (a.order > b.order) return 1;
          if (a.order < b.order) return -1;
          return 0;
        })
        .map((pointOrder) => pointOrder.trackingPointId);

      const orderAndIds = sequenceType.pointOrder.map((pointOrder) => {
        return {
          id: pointOrder.trackingPointId,
          order: pointOrder.order,
        };
      });

      let trackingPoints = await findByIds(ids);

      const filteredItems = await getFilteredItems();
      if (filteredItems && filteredItems.length > 0 && filteredItems[0].data) {
        const filteredTrackingPoints = filteredItems[0].data.trackingPoints;
        trackingPoints = trackingPoints.filter((point) => {
          return filteredTrackingPoints.some((tp) => tp === point.id);
        });
      }

      const aa = trackingPoints
        .map((point) => {
          return {
            title: point.name,
            order: orderAndIds.find((orderAndId) => orderAndId.id === point.id)
              ?.order,
            slug: item.slug,
            trackingPoint: {
              id: point.id,
              name: point.name,
            },
            additionalData: {
              typeId,
            },
          };
        })
        .sort((a, b) => {
          if (a.order > b.order) return 1;
          if (a.order < b.order) return -1;
          return 0;
        });
      console.log("Tracking Points:", aa);
      return aa;
    } else if (sequenceType?.isAggregation) {
      const slug = item.slug;

      const dd = sequenceType.aggregationOrder.map((order) => {
        return {
          title: order?.aggregation?.name || "Desconhecido",
          isAggregation: true,
          additionalData: {
            aggregation: order?.aggregation,
            typeId: order?.aggregation?.typeId,
            aggregationPairs: order?.sequenceAggregationPairOrder,
          },
          type: order,
          slug,
        };
      });

      return dd;
    } else {
      const slug = item.slug;

      const findByType = async () => {
        const type = await typesService.getBySlug(slug);

        // 🛡️ Proteção: se tipo não existe no IndexedDB local, retorna array vazio
        if (!type || !type.id) {
          //console.warn(
          //  `⚠️ Tipo '${slug}' não encontrado no IndexedDB local (agregação)`
          //);
          return [];
        }

        return paresService.findParesByTypeId(type.id);
      };
      const results = await findByType();

      const dd = sequenceType.biomagneticPairTypesOrder.map((order) => {
        const item = results.find((item) => item.id === order.parId);
        return {
          title: item?.name || "Desconhecido",
          isAggregation: false,
          type: item,
          slug,
        };
      });

      console.log("Pares results:", dd);
      return dd;
    }
  },

  /**
   * Pares que vem das categorias, sintomas e patógenos
   * @param state
   * @param item
   * @returns {Promise<*>}
   */
  async [_ACTIONS.STEPS_OTHER_PROPERTIES]({ state }, item) {
    const propertyMapToFindParesBySlug = new Map([
      ["category", () => categoriesService.findOne(item.id)],
      ["symptom", () => symptomsService.findOne(item.id)],
      ["pathogen", () => pathogensService.findOne(item.id)],
    ]);

    const functionToGetPopulatedProperty = propertyMapToFindParesBySlug.get(
      item.property.slug
    );

    const result = await functionToGetPopulatedProperty();

    const pointsOrderOfProperty = await pointsOrderService.findByIds(
      result.pares.map((par) => par.points.id)
    );

    const promiseToPopulatePoints = pointsOrderOfProperty.map(
      async (pointOrder) => {
        return {
          trackingPoint: await pointsService.findOne(
            pointOrder.trackingPointId
          ),
          impactionPoint: await pointsService.findOne(
            pointOrder.impactionPointId
          ),
          ...pointOrder,
        };
      }
    );

    let pointsOrder = await Promise.all(promiseToPopulatePoints);

    const filteredItems = await getFilteredItems();
    if (filteredItems && filteredItems.length > 0 && filteredItems[0].data) {
      const filteredPairs = filteredItems[0].data.pares;

      pointsOrder = pointsOrder.filter((point) => {
        return filteredPairs.some((pair) => pair === point.parId);
      });
    }

    const { isSecondLevel: isParSecondLevel } = item;

    return _(pointsOrder)
      .groupBy((pointOrder) => {
        return pointOrder.trackingPointId + "-" + pointOrder.par.typeId;
      })
      .map((pointsOrder) => {
        return {
          title: isParSecondLevel
            ? pointsOrder[0].par.name
            : pointsOrder[0].trackingPoint.name,
          slug: SlugEnum.PARES_ADICIONADOS_POR_PROPRIEDADES,
          par: pointsOrder[0].par,
          trackingPoint: pointsOrder[0].trackingPoint,
          impactionPoints: pointsOrder.map((pointOrder) => ({
            pointOrderId: pointOrder.id,
            ...pointOrder.impactionPoint,
          })),
        };
      })
      .value();
  },
  async [_ACTIONS.OPEN_DETAIL_DIALOG]({ commit, dispatch }, step) {
    dispatch("loading/openDialog", null, { root: true });
    const mapDialogs = new Map([
      // Custom types
      [
        SlugEnum.FENOMENOS_TUMORAIS,
        (step) => {
          dispatch("tumoralPhenomena/selectTumoralPhenomenon", step.type, {
            root: true,
          });
        },
      ],
      [
        SlugEnum.FENOMENOS_VASCULARES,
        (step) => {
          dispatch("vascularPhenomena/selectOne", step.type, { root: true });
        },
      ],
      [
        SlugEnum.ESPACOS_POTENCIAIS,
        (step) => {
          dispatch("potentialSpaces/selectOne", step.type, { root: true });
        },
      ],
      [
        SlugEnum.TIPOS_DE_FLUXO,
        (step) => {
          dispatch("flowTypes/selectOne", step.type, { root: true });
        },
      ],
      [
        SlugEnum.OLIGOELEMENTOS,
        (step) => {
          dispatch("oligoelements/selectType", step.type, {
            root: true,
          });
        },
      ],
      [
        SlugEnum.CHAKRAS,
        (step) => {
          dispatch("chakras/selectOne", step.type, {
            root: true,
          });
        },
      ],
      [
        SlugEnum.CROMOSSOMOS,
        (step) => {
          dispatch("chromosomes/selectOne", step.type, {
            root: true,
          });
        },
      ],
      [
        SlugEnum.TERAPIAS_COMPLEMENTARES,
        (step) => {
          dispatch("complementaryTherapies/selectOne", step.type, {
            root: true,
          });
        },
      ],

      // Traking points types
      [
        SlugEnum.RASTREIO,
        (step) => {
          console.log("[RASTREIO] Step recebido:", step);
          if (step.trackingPoint && step.additionalData) {
            commit("points/setSelectedTypeId", step.additionalData.typeId, {
              root: true,
            });
            commit("points/setSelectedTrackingPoint", step.trackingPoint, {
              root: true,
            });

            dispatch("points/loadImpactionPointsWithPar", null, { root: true });
          } else {
            console.warn("[RASTREIO] Faltam dados:", {
              hasTrackingPoint: !!step.trackingPoint,
              hasAdditionalData: !!step.additionalData,
            });
          }
        },
      ],
      [
        SlugEnum.RESERVATORIO,
        (step) => {
          console.log("[RESERVATORIO] Step recebido:", step);
          if (step.trackingPoint && step.additionalData) {
            commit("points/setSelectedTypeId", step.additionalData.typeId, {
              root: true,
            });
            commit("points/setSelectedTrackingPoint", step.trackingPoint, {
              root: true,
            });

            dispatch("points/loadImpactionPointsWithPar", null, { root: true });
          } else {
            console.warn("[RESERVATORIO] Faltam dados:", {
              hasTrackingPoint: !!step.trackingPoint,
              hasAdditionalData: !!step.additionalData,
            });
          }
        },
      ],

      // Biomagnetic pairs types
      [
        SlugEnum.PARES_PSICOEMOCIONAIS,
        (step) => {
          dispatch("psycoemotionals/selectOne", step.type, {
            root: true,
          });
        },
      ],
      [
        SlugEnum.INTOXICACAO,
        (step) => {
          dispatch("poisoning/selectOne", step.type, {
            root: true,
          });
        },
      ],
      [
        SlugEnum.HORMONAIS,
        (step) => {
          dispatch("hormonal/selectOne", step.type, {
            root: true,
          });
        },
      ],
      [
        SlugEnum.ESPIRITUAIS_E_MALIGNOS,
        (step) => {
          dispatch("spiritual/selectOne", step.type, {
            root: true,
          });
        },
      ],
      [
        SlugEnum.SOFROLOGICO,
        (step) => {
          dispatch("sophrological/selectOne", step.type, {
            root: true,
          });
        },
      ],
      [
        SlugEnum.HERPES_CHECAGEM,
        (step) => {
          dispatch("herpesCheck/selectOne", step.type, {
            root: true,
          });
        },
      ],
      [
        SlugEnum.MOVIMENTO_3D1_3D2,
        (step) => {
          dispatch("movement3D12/selectOne", step.type, {
            root: true,
          });
        },
      ],
      [
        SlugEnum.PROTOCOLO_BASICO,
        (step) => {
          dispatch("basicProtocol/selectOne", step.type, {
            root: true,
          });
        },
      ],
      [
        SlugEnum.PROTOCOLO_LINFATICO,
        (step) => {
          dispatch("lymphaticProtocol/selectOne", step.type, {
            root: true,
          });
        },
      ],
      [
        SlugEnum.PROTOCOLO_FSMI,
        (step) => {
          dispatch("fsmiProtocol/selectOne", step.type, {
            root: true,
          });
        },
      ],
      [
        SlugEnum.PARES_ESPECIAIS_NIVEL_1_2,
        (step) => {
          dispatch("specialPairs/selectOne", step.type, {
            root: true,
          });
        },
      ],
      [
        SlugEnum.PARES_ADICIONADOS_POR_PROPRIEDADES,
        (step) => {
          console.log("Pares adicionados por propriedades", step);
          dispatch("dynamicPairType/selectOne", step.par, {
            root: true,
          });
        },
      ],
    ]);

    if (step?.isAggregation && step.additionalData) {
      commit("points/setSelectedTypeId", step.additionalData.typeId, {
        root: true,
      });

      const pairs = await paresService.findParesByTypeId(
        step.additionalData.typeId
      );

      let filteredPairs = pairs.filter((pair) => {
        return step.additionalData.aggregationPairs.some(
          (aggregationPair) => aggregationPair.parId === pair.id
        );
      });

      if (filteredPairs.length > 1) {
        filteredPairs = filteredPairs.sort((a, b) => {
          const orderA = step.additionalData.aggregationPairs.find(
            (aggregationPair) => aggregationPair.parId === a.id
          )?.order;
          const orderB = step.additionalData.aggregationPairs.find(
            (aggregationPair) => aggregationPair.parId === b.id
          )?.order;
          return orderA - orderB;
        });
      }

      commit("points/setSelectedAggregationPairs", filteredPairs, {
        root: true,
      });

      commit("points/setSelectedAggregation", step.additionalData.aggregation, {
        root: true,
      });

      dispatch("points/loadPairsWithAggregation", step.type, {
        root: true,
      });
    } else {
      let showDialog = mapDialogs.get(step.slug);

      if (!showDialog) {
        showDialog = mapDialogs.get(
          SlugEnum.PARES_ADICIONADOS_POR_PROPRIEDADES
        );
      }

      await showDialog(step);
    }

    dispatch("loading/closeDialog", null, { root: true });
  },
};
